/**
 * "Message" on a coach's profile: opens (or starts) the one-to-one chat with
 * them. Shown only where direct messages are on (dmsAvailable), never to
 * yourself, and not for a coach you've blocked: they get "Unblock" instead,
 * which is the way back from a block made in a post or a chat.
 */
import React, { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import { GOLD } from "../constants/theme";
import { startDm } from "./chatApi";
import { dmsAvailable } from "../utils/featureFlags";
import { getBlockedIds, unblockUser } from "../community/blocklist";
import { bamToast } from "../shared/dialog";

export function MessageCoachButton({ userId, onOpened, go, label = "Message" }) {
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(() => getBlockedIds().includes(userId));
  useEffect(() => {
    const sync = () => setBlocked(getBlockedIds().includes(userId));
    window.addEventListener("bam-blocklist-changed", sync);
    return () => window.removeEventListener("bam-blocklist-changed", sync);
  }, [userId]);

  if (!userId || !dmsAvailable()) return null;

  const style = { flex: 1, minHeight: 44, borderRadius: 11, fontSize: 14, fontWeight: 800, cursor: busy ? "default" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit" };

  if (blocked) {
    return (
      <button type="button" className="btn" onClick={() => { unblockUser(userId); bamToast("Unblocked."); }}
        style={{ ...style, background: "transparent", color: "inherit", border: "1px solid rgba(255,255,255,0.18)" }}>
        Unblock
      </button>
    );
  }

  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const chatId = await startDm(userId);
      try { sessionStorage.setItem("bam_open_chat", chatId); } catch { /* private window */ }
      onOpened?.();
      if (go) go("chats"); else window.location.hash = "#/chats";
    } catch (e) {
      bamToast(e.message || "Couldn't start the chat.", { tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" className="btn" onClick={open} disabled={busy}
      style={{ ...style, background: GOLD, color: "#111", border: "none", opacity: busy ? 0.7 : 1 }}>
      <MessageSquare size={16}/> {busy ? "Opening…" : label}
    </button>
  );
}

export default MessageCoachButton;
